import { Typography, Icon, Button } from "@material-ui/core";
import { formatDate } from "app/main/utils/dateUtils";
import { useMemo, useState } from "react";
import { useSnackbar } from "notistack";
import moment from "moment";

import { initiatePhoneCall, initiateEmailContact } from "app/services/brokersServices";
import { isEnabled } from "app/services/featureStorageService";
import { showSnackbar } from "app/main/utils/snackbarUtil";
import { updateLoad } from "app/main/search/result/LoadSearchViewHelper";
import { useSelector } from "react-redux";
import LoadSearchContactConfirmModalView from "./LoadSearchContactConfirmModalView";

function LoadSearchSummaryBrokerView(props) {
	const load = props.load;
	const carrierInfo = props.carrierInfo;
	const trailerDescription = props.trailerDescription;
	const carrierId = props.carrierId;
	const searchId = props.searchId;
	const truckId = props.truckId;
	const relayChain = props.relayChain;
	const userId = props.userId;
	const onShowBrokerSummary = props.onShowBrokerSummary;
	const snackbar = useSnackbar();
	const socket = useSelector(({ socket }) => socket.connection.socket);

	// State for contact confirmation modal
	const [contactModalOpen, setContactModalOpen] = useState(false);
	const [contactType, setContactType] = useState(null); // "email" or "phone"

	// Improved Regex to check for phone numbers, including optional +, spaces, dashes, and extensions (e.g., +18005803101, (800) 580-3101 ext 1234, +17122567260 ext. 1316)
	const isPhone =
		load?.contact?.match(/^\+?\d[\d\s\-().]*(?:\s*(?:ext\.?|x)\s*\d+)?$/i) && !load?.contact?.includes("@")
			? true
			: false;

	const mailContent = useMemo(() => {
		const newLine = `\n`;
		const bullet = `-`;
		const brokerContact = load?.contact;
		const mailSubject = `Load ${load?.origin_state} to ${load?.destination_state} <> ${carrierInfo?.carrier_company_name}`;
		const mailBody = `Hey. Would like to book your load. Let us know the rate and details on the load${newLine}${newLine}Load: ${
			load?.hasBrokerId ? load.load_id : ""
		} ${newLine}${bullet} Pick up: ${load?.origin} on ${moment(load.ready).format(
			"MMM DD"
		)}${newLine}${bullet} Delivery: ${load?.destination}${newLine}MC - ${
			carrierInfo?.mcnumber || "N/A"
		}${newLine}${bullet} ${carrierInfo?.carrier_company_name || "N/A"}${newLine}${bullet} ${
			trailerDescription || "N/A"
		}${newLine}${bullet} ${carrierInfo?.carrier_email || "N/A"}`;
		return { to: brokerContact, subject: mailSubject, body: mailBody };
	}, [load, trailerDescription, carrierInfo]);

	const brokerLink = useMemo(() => {
		if (!load?.contact?.includes("@")) return `tel:${load?.contact}`;
		return `mailto:${load?.contact}?subject=${mailContent.subject}&body=${mailContent.body}`;
	}, [load, mailContent]);

	const relayTruckId = useMemo(() => {
		return relayChain?.pickup?.truck_id;
	}, [relayChain]);

	const searchMode = relayChain ? "relay_chain" : "live_mode";

	const handleCopyMailBody = () => {
		navigator.clipboard.writeText(mailContent.to + "\n\n" + mailContent.subject + "\n\n" + mailContent.body);
		showSnackbar(snackbar, "Email body copied to clipboard", "success");
	};

	// Open modal handlers
	const handleCallContact = () => {
		setContactType("phone");
		setContactModalOpen(true);
	};

	const handleEmailContact = () => {
		setContactType("email");
		setContactModalOpen(true);
	};

	// Close modal handler
	const handleCloseContactModal = () => {
		setContactModalOpen(false);
		setContactType(null);
	};

	// Actual contact execution after confirmation
	const handleConfirmContact = async ({ contact: confirmedContact, minAllowed, goal }) => {
		setContactModalOpen(false);

		if (contactType === "phone") {
			// Execute phone call
			try {
				// Send relay truck ID if available, otherwise use truck ID
				await initiatePhoneCall(
					userId,
					confirmedContact,

					load?.tripid,
					carrierId,
					searchId,
					relayTruckId || truckId,
					minAllowed,
					goal,
					searchMode
				);
				console.log("Call initiated successfully");
				showSnackbar(snackbar, "Call initiated successfully", "success");

				// Update load contacted status
				const currentTimestamp = moment().unix();
				const updateData = { contacted: currentTimestamp };

				// Update load in redux store
				updateLoad(searchId, load.tripid, updateData);

				// Send socket message to update other clients
				socket.emit("message", {
					type: "SEARCH_UPDATED_LOAD",
					data: {
						userId: userId,
						searchId: searchId,
						tripid: load.tripid,
						data: updateData,
					},
				});
			} catch (error) {
				console.error("Failed to initiate call:", error);
				showSnackbar(snackbar, "Failed to initiate call", "error");
			}
		} else if (contactType === "email") {
			// Execute email send
			try {
				// Send relay truck ID if available, otherwise use truck ID
				await initiateEmailContact(
					userId,
					confirmedContact,
					load?.tripid,
					carrierId,
					searchId,
					relayTruckId || truckId,
					minAllowed,
					goal,
					searchMode
				);
				console.log("Email sent successfully");
				showSnackbar(snackbar, "Email sent successfully", "success");

				// Update load contacted status
				const currentTimestamp = moment().unix();
				const updateData = { email_sent: currentTimestamp };

				// Update load in redux store
				updateLoad(searchId, load.tripid, updateData);

				// Send socket message to update other clients
				socket.emit("message", {
					type: "SEARCH_UPDATED_LOAD",
					data: {
						userId: userId,
						searchId: searchId,
						tripid: load.tripid,
						data: updateData,
					},
				});
			} catch (error) {
				console.error("Failed to send email:", error);
				showSnackbar(snackbar, "Failed to send email", "error");
			}
		}

		setContactType(null);
	};

	return (
		<div className={"flex flex-col mb-28 -mt-10 "}>
			<div className={"flex flex-row border-b-1 border-grey-300 mt-10"}>
				<Typography className="flex text-black text-12 ml:text-13 break-normal">Name</Typography>
				<Typography
					className={`text-black text-12 ml:text-13 w-full ${
						props?.isPublicView ? "" : "cursor-pointer text-blue-700"
					} ${load?.brokerBlacklisted ? "line-through" : ""}`}
					style={{ textAlign: "end", padding: 0 }}
					onClick={props?.isPublicView ? null : onShowBrokerSummary}
				>
					{load?.clientid && load?.clientidOverride
						? `${load?.clientid} (${load?.contact_name})`
						: load?.clientid ?? "No Information"}
					{load?.clientid && <Icon className="text-15 ml-4 -mb-2">info</Icon>}
				</Typography>
			</div>
			<div className={"flex flex-row border-b-1 border-grey-300 mt-10"}>
				<Typography className="flex w-full text-black text-12 ml:text-13 break-normal">Contact</Typography>
				{load?.contact ? (
					<div className="flex items-center">
						<Typography
							onClick={() => window.open(brokerLink)}
							className={`text-12 ml:text-13 cursor-pointer text-blue-700 mr-2`}
							style={{ textAlign: "end", padding: 0 }}
						>
							{load?.contact}
						</Typography>
						{load?.contact?.includes("@") && (
							<Typography className="text-15 ml-4 -mb-2" variant="contained" onClick={handleCopyMailBody}>
								<Icon className="text-15 ml-4 -mb-2">content_copy</Icon>
							</Typography>
						)}
					</div>
				) : (
					"-"
				)}
			</div>
			{isEnabled("ENABLE_PHONE_CALLS") && (
				<>
					<div className={"flex flex-row border-b-1 border-grey-300 mt-10"}>
						<div className="flex items-center w-full">
							<Typography className="flex w-full text-black text-12 ml:text-13 break-normal">Email Agent</Typography>
							<div className="flex items-center gap-2 ml-auto">
								<Button
									className="my-2 py-1 px-15 text-11 ml:text-12 bg-green-700 text-white h-24 ml:h-28"
									variant="contained"
									onClick={handleEmailContact}
									startIcon={<Icon className="text-12">email</Icon>}
								>
									Send
								</Button>
							</div>
						</div>
					</div>
					<div className={"flex flex-row border-b-1 border-grey-300 mt-10"}>
						<div className="flex items-center w-full">
							<Typography className="flex w-full text-black text-12 ml:text-13 break-normal">Voice Agent</Typography>
							<div className="flex items-center gap-2 ml-auto">
								<Button
									className="my-2 py-1 px-15 text-11 ml:text-12 bg-blue-700 text-white h-24 ml:h-28"
									variant="contained"
									onClick={() => handleCallContact(true)}
									startIcon={<Icon className="text-12">support_agent</Icon>}
								>
									Call
								</Button>
								{/*<Button
								className="my-2 py-1 px-15 text-11 ml:text-12 bg-secondary text-white h-24 ml:h-28"
								variant="contained"
								onClick={() => handleCallContact(false)}
								startIcon={<Icon className="text-12">phone</Icon>}
							>
								Manual
							</Button>*/}
							</div>
						</div>
					</div>
				</>
			)}

			{/* Contact Confirmation Modal */}
			<LoadSearchContactConfirmModalView
				open={contactModalOpen}
				contactType={contactType}
				contactValue={contactType === "email" ? load?.email || load?.contact : load?.contact}
				loadInfo={load}
				onClose={handleCloseContactModal}
				onConfirm={handleConfirmContact}
			/>
		</div>
	);
}

export default LoadSearchSummaryBrokerView;
